import React from "react";
import {
  BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, LabelList,
} from "recharts";
import {
  ChartBar, Warning, Bug, Lightbulb, Eye, PlugsConnected, Heartbeat, ClipboardText, ArrowUpRight, ArrowDownRight,
} from "@phosphor-icons/react";

/* Static recharts style configs — module-scope so they aren't re-allocated
   on every render (referential stability lets recharts skip child re-mounts). */
const AXIS_TICK = { fontSize: 11, fill: "#52525B" };
const TOOLTIP_CURSOR = { fill: "rgba(0, 0, 0, 0.04)" };
const TOOLTIP_STYLE = { background: "#09090B", border: "none", color: "#fff", fontSize: 12 };
const BAR_CHART_MARGIN = { top: 10, right: 10, left: 0, bottom: 0 };
const BAR_RADIUS = [2, 2, 0, 0];
const LABEL_STYLE = { fontSize: 11, fontWeight: 700, fill: "#09090B" };
const LEGEND_STYLE = { fontSize: 11 };
const PIE_TOOLTIP_STYLE = { border: "1px solid #E4E4E7", fontSize: 12 };

/** KPI tile — large number + label + delta hint */
function KpiTile({ label, value, sub, accent = "#0020F5", icon: Icon, testid }) {
  return (
    <div className="bg-white border border-zinc-200 p-5" data-testid={testid}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-500">{label}</div>
          <div className="font-display font-black text-3xl mt-2 text-zinc-950" style={{ letterSpacing: "-0.02em" }}>{value}</div>
          {sub && <div className="text-xs text-zinc-500 mt-1.5">{sub}</div>}
        </div>
        {Icon && (
          <div className="w-10 h-10 grid place-items-center shrink-0" style={{ background: `${accent}15` }}>
            <Icon size={20} weight="duotone" style={{ color: accent }} />
          </div>
        )}
      </div>
    </div>
  );
}

/** Health-score gauge ring 0..100 */
function HealthGauge({ score = 0 }) {
  const clamped = Math.max(0, Math.min(100, score));
  let color = "#16A34A";
  let label = "Healthy";
  if (clamped < 40) { color = "#FF3B30"; label = "At risk"; }
  else if (clamped < 70) { color = "#FF9500"; label = "Watch"; }
  const data = [
    { name: "score", value: clamped, color },
    { name: "rest", value: 100 - clamped, color: "#F4F4F5" },
  ];
  return (
    <div className="bg-white border border-zinc-200 p-5 lg:row-span-2" data-testid="dash-health-gauge">
      <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-500">Project health</div>
      <div className="mt-2 relative">
        <ResponsiveContainer width="100%" height={220}>
          <PieChart>
            <Pie
              data={data}
              dataKey="value"
              cx="50%"
              cy="50%"
              innerRadius={70}
              outerRadius={95}
              startAngle={90}
              endAngle={-270}
              isAnimationActive={false}
              stroke="none"
            >
              {data.map((d) => <Cell key={d.name} fill={d.color} />)}
            </Pie>
          </PieChart>
        </ResponsiveContainer>
        <div className="absolute inset-0 grid place-items-center pointer-events-none">
          <div className="text-center">
            <div className="font-display font-black text-5xl text-zinc-950" style={{ color }}>{clamped}</div>
            <div className="text-[10px] tracking-[0.2em] uppercase font-bold mt-1" style={{ color }}>{label}</div>
          </div>
        </div>
      </div>
      <p className="text-xs text-zinc-500 mt-2 leading-relaxed">
        Deterministic score: 100 minus penalties for critical/high risks, open issues, unresolved blockers, and weak assumptions.
      </p>
    </div>
  );
}

function ChartCard({ title, eyebrow, children, testid, action }) {
  return (
    <div className="bg-white border border-zinc-200 p-5" data-testid={testid}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-500">{eyebrow}</div>
          <div className="font-display font-bold text-base mt-0.5">{title}</div>
        </div>
        {action}
      </div>
      <div className="mt-3">{children}</div>
    </div>
  );
}

function BarBySeverity({ data, testid }) {
  if (!data?.length || data.every((d) => !d.value)) {
    return <div className="text-xs text-zinc-500 py-12 text-center font-mono">No data yet — add items or upload a file.</div>;
  }
  return (
    <ResponsiveContainer width="100%" height={220}>
      <BarChart data={data} margin={BAR_CHART_MARGIN} data-testid={testid}>
        <CartesianGrid stroke="#F4F4F5" vertical={false} />
        <XAxis dataKey="name" tick={AXIS_TICK} axisLine={false} tickLine={false} />
        <YAxis allowDecimals={false} tick={AXIS_TICK} axisLine={false} tickLine={false} />
        <Tooltip cursor={TOOLTIP_CURSOR} contentStyle={PIE_TOOLTIP_STYLE} />
        <Bar dataKey="value" radius={BAR_RADIUS}>
          <LabelList dataKey="value" position="top" style={LABEL_STYLE} />
          {data.map((d) => <Cell key={d.name} fill={d.color} />)}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

function DonutChart({ data, testid }) {
  if (!data?.length || data.every((d) => !d.value)) {
    return <div className="text-xs text-zinc-500 py-12 text-center font-mono">No data yet.</div>;
  }
  return (
    <ResponsiveContainer width="100%" height={220}>
      <PieChart data-testid={testid}>
        <Pie data={data} dataKey="value" cx="50%" cy="50%" innerRadius={50} outerRadius={80} paddingAngle={2} isAnimationActive={false}>
          {data.map((d) => <Cell key={d.name} fill={d.color} stroke="white" strokeWidth={2} />)}
        </Pie>
        <Tooltip contentStyle={PIE_TOOLTIP_STYLE} />
        <Legend
          iconType="square"
          iconSize={10}
          wrapperStyle={LEGEND_STYLE}
          formatter={(v, e) => `${v} · ${e.payload.value}`}
        />
      </PieChart>
    </ResponsiveContainer>
  );
}

/** Solution matrix: 3x3 effort × impact heatmap */
function SolutionMatrix({ cells, testid }) {
  if (!cells?.length || cells.every((c) => !c.count)) {
    return <div className="text-xs text-zinc-500 py-8 text-center font-mono">No solutions generated yet.</div>;
  }
  const order = ["low", "medium", "high"];
  const get = (eff, imp) => cells.find((c) => c.effort === eff && c.impact === imp)?.count || 0;
  const max = Math.max(...cells.map((c) => c.count), 1);
  return (
    <div className="grid grid-cols-[auto_1fr_1fr_1fr] gap-2 text-xs" data-testid={testid}>
      <div />
      {order.map((imp) => <div key={imp} className="text-center font-mono uppercase tracking-widest text-zinc-500">{imp} impact</div>)}
      {order.slice().reverse().map((eff) => (
        <React.Fragment key={eff}>
          <div className="text-right pr-2 font-mono uppercase tracking-widest text-zinc-500 self-center">{eff} effort</div>
          {order.map((imp) => {
            const n = get(eff, imp);
            const opacity = 0.15 + 0.85 * (n / max);
            return (
              <div
                key={`${eff}-${imp}`}
                className="aspect-square grid place-items-center font-display font-black text-2xl"
                style={{ background: `rgba(0, 32, 245, ${opacity})`, color: n > 0 ? (opacity > 0.5 ? "white" : "#0020F5") : "#D4D4D8" }}
                data-testid={`matrix-${eff}-${imp}`}
              >
                {n}
              </div>
            );
          })}
        </React.Fragment>
      ))}
      <div className="col-span-4 text-[10px] tracking-[0.15em] uppercase text-zinc-500 mt-2 font-mono">
        Sweet spot = high impact + low effort (bottom-right of an inverted matrix)
      </div>
    </div>
  );
}

/* ---------- Top-risks + top-issues tables ---------- */

const sevColor = {
  critical: "#FF3B30", high: "#FF9500", medium: "#FFD60A", low: "#16A34A",
};

function TopRisksTable({ risks }) {
  if (!risks?.length) return <div className="text-xs text-zinc-500 py-6 text-center font-mono">No risks yet.</div>;
  return (
    <table className="w-full text-sm" data-testid="dash-top-risks">
      <thead className="text-[10px] tracking-[0.15em] uppercase text-zinc-500 border-b border-zinc-200">
        <tr><th className="text-left py-2 w-8">#</th><th className="text-left py-2">Risk</th><th className="text-left py-2 w-24">Severity</th><th className="text-left py-2 w-16">I × L</th></tr>
      </thead>
      <tbody>
        {risks.map((r, i) => {
          const sev = (r.severity || "medium").toLowerCase();
          return (
            <tr key={r.id || i} className="border-b border-zinc-100 last:border-0">
              <td className="py-2 font-mono text-zinc-500">{i + 1}</td>
              <td className="py-2 pr-3 text-zinc-900">
                <div className="font-bold">{r.title}</div>
                {r.suggested_mitigation && <div className="text-xs text-zinc-500 mt-0.5 line-clamp-2">{r.suggested_mitigation}</div>}
              </td>
              <td className="py-2">
                <span className="inline-block px-2 py-0.5 text-[10px] font-bold tracking-[0.1em] uppercase text-white" style={{ background: sevColor[sev] || "#71717A" }}>{sev}</span>
              </td>
              <td className="py-2 font-mono text-zinc-700">{(r.impact ?? "—")} × {(r.likelihood ?? "—")}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function TopIssuesTable({ issues }) {
  if (!issues?.length) return <div className="text-xs text-zinc-500 py-6 text-center font-mono">No open issues.</div>;
  return (
    <table className="w-full text-sm" data-testid="dash-top-issues">
      <thead className="text-[10px] tracking-[0.15em] uppercase text-zinc-500 border-b border-zinc-200">
        <tr><th className="text-left py-2 w-8">#</th><th className="text-left py-2">Issue</th><th className="text-left py-2 w-24">Urgency</th><th className="text-left py-2 w-24">Status</th></tr>
      </thead>
      <tbody>
        {issues.map((iss, i) => {
          const urg = (iss.urgency || "medium").toLowerCase();
          return (
            <tr key={iss.id || i} className="border-b border-zinc-100 last:border-0">
              <td className="py-2 font-mono text-zinc-500">{i + 1}</td>
              <td className="py-2 pr-3 text-zinc-900">
                <div className="font-bold">{iss.title}</div>
                {iss.root_cause && <div className="text-xs text-zinc-500 mt-0.5 line-clamp-2">{iss.root_cause}</div>}
              </td>
              <td className="py-2">
                <span className="inline-block px-2 py-0.5 text-[10px] font-bold tracking-[0.1em] uppercase text-white" style={{ background: sevColor[urg] || "#71717A" }}>{urg}</span>
              </td>
              <td className="py-2 font-mono text-zinc-700 uppercase text-xs">{(iss.status || "open").replace("_", " ")}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

/* ---------- Main dashboard component ---------- */

export default function BriefDashboard({ data }) {
  const [page, setPage] = React.useState("summary");
  if (!data) return null;
  const stats = data.stats || {};
  const charts = data.charts || {};
  const ai = data.ai || {};

  const ArrowDelta = stats.issues_open > 0 ? ArrowUpRight : ArrowDownRight;
  const issueDeltaColor = stats.issues_open > 0 ? "#FF3B30" : "#16A34A";
  const healthScore = data.health_score ?? stats.health_score ?? 0;

  /* PowerBI-style page tabs */
  const Tab = ({ id, label, n }) => {
    const active = page === id;
    return (
      <button
        type="button"
        onClick={() => setPage(id)}
        className={`px-5 h-11 inline-flex items-center gap-2 text-xs font-bold tracking-[0.15em] uppercase border ${
          active
            ? "bg-zinc-950 text-white border-zinc-950"
            : "bg-white text-zinc-700 border-zinc-300 hover:border-zinc-950"
        }`}
        data-testid={`brief-tab-${id}`}
      >
        <span className="font-mono text-[10px] opacity-70">{n}</span>
        {label}
      </button>
    );
  };

  return (
    <div className="space-y-4" data-testid="brief-dashboard">
      {/* Page tabs — PowerBI-style 2 pages */}
      <div className="flex items-center gap-2 border-b border-zinc-200 pb-3" data-testid="brief-page-tabs">
        <Tab id="summary" label="Executive summary" n="01" />
        <Tab id="charts"  label="Charts & details"  n="02" />
        <span className="ml-auto text-[10px] font-mono text-zinc-500 hidden sm:inline">
          Health: <b className="text-zinc-900">{healthScore}/100</b> · Generated {new Date(data.generated_at || Date.now()).toLocaleString()}
        </span>
      </div>

      {page === "summary" && (
        <div className="space-y-4" data-testid="brief-page-summary">
          {/* Top KPI strip — 5 across */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
            <KpiTile label="Assumptions"  value={stats.assumptions_total ?? 0} sub="6-lens framework"           icon={Eye}             accent="#0020F5" testid="kpi-assumptions" />
            <KpiTile label="Total risks"  value={stats.risks_total ?? 0}        sub={`${(charts.risks_by_severity || []).filter((d) => ["Critical", "High"].includes(d.name)).reduce((a, b) => a + b.value, 0)} critical/high`} icon={Warning} accent="#FF9500" testid="kpi-risks" />
            <KpiTile label="Open issues"  value={stats.issues_open ?? 0}        sub={<span className="inline-flex items-center gap-1"><ArrowDelta size={12} weight="bold" style={{ color: issueDeltaColor }} /> of {stats.issues_total ?? 0} total</span>} icon={Bug} accent="#FF3B30" testid="kpi-issues" />
            <KpiTile label="Solutions"    value={stats.solutions_total ?? 0}    sub="AI-proposed mitigations"   icon={Lightbulb}       accent="#16A34A" testid="kpi-solutions" />
            <KpiTile label="Integrations" value={data.integrations_count ?? 0}  sub="Source files ingested"     icon={PlugsConnected}  accent="#7C3AED" testid="kpi-integrations" />
          </div>

          {/* Health gauge spans full row alongside the AI headline */}
          <div className="grid lg:grid-cols-[1fr_2fr] gap-4">
            <HealthGauge score={healthScore} />
            <div className="bg-zinc-950 text-white p-6" data-testid="dash-ai-headline">
              <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-[#9DA8FF] inline-flex items-center gap-2"><Heartbeat size={12} weight="duotone" /> Executive headline</div>
              <p className="font-display font-bold text-2xl mt-3 leading-tight">{ai.headline || "Run the brief to generate an AI headline."}</p>
              {(ai.alerts || []).length > 0 && (
                <div className="mt-5 space-y-2.5">
                  <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-[#9DA8FF]">Critical alerts</div>
                  {(ai.alerts || []).slice(0, 3).map((a, i) => (
                    <div key={`alert-${i}`} className="flex items-start gap-3 text-sm">
                      <span className="w-2 h-2 mt-1.5 bg-[#FF9500] shrink-0" />
                      <span className="text-zinc-100">{a}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Top priorities — readable card grid */}
          {(ai.top_priorities || []).length > 0 && (
            <div className="bg-white border border-zinc-200 p-6">
              <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-500 flex items-center gap-2"><ClipboardText size={12} weight="duotone" /> Top priorities & resolutions</div>
              <div className="mt-4 grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {ai.top_priorities.slice(0, 6).map((p, i) => (
                  <div key={`prio-${i}`} className="border-l-2 border-[#0020F5] pl-4 py-1" data-testid={`dash-priority-${i}`}>
                    <div className="font-display font-bold text-sm text-zinc-950">{p.title}</div>
                    <p className="text-xs text-zinc-700 mt-1.5 leading-relaxed">{p.resolution}</p>
                    <div className="text-[10px] font-mono text-zinc-500 mt-2">{p.owner_role} · {p.deadline_hint}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Recommendations list (if present) */}
          {(ai.recommendations || []).length > 0 && (
            <div className="bg-white border border-zinc-200 p-6">
              <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-500">Recommendations</div>
              <ul className="mt-3 space-y-2 text-sm text-zinc-800">
                {(ai.recommendations || []).slice(0, 6).map((r, i) => (
                  <li key={`rec-${i}`} className="flex gap-3"><span className="font-mono text-zinc-400">{String(i + 1).padStart(2, "0")}</span><span>{typeof r === "string" ? r : r.text || ""}</span></li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {page === "charts" && (
        <div className="space-y-4" data-testid="brief-page-charts">
          {/* Bars row — risks & issues severity */}
          <div className="grid lg:grid-cols-2 gap-4">
            <ChartCard eyebrow="Risk lens" title="Risks by severity" testid="chart-risks-by-severity">
              <BarBySeverity data={charts.risks_by_severity} testid="bar-risks-by-severity" />
            </ChartCard>
            <ChartCard eyebrow="Issue lens" title="Issues by urgency" testid="chart-issues-by-urgency">
              <BarBySeverity data={charts.issues_by_urgency} testid="bar-issues-by-urgency" />
            </ChartCard>
          </div>

          {/* Distribution donuts + matrix */}
          <div className="grid lg:grid-cols-3 gap-4">
            <ChartCard eyebrow="Assumption lens" title="Validity score distribution" testid="chart-assumption-dist">
              <DonutChart data={charts.assumption_distribution} testid="donut-assumptions" />
            </ChartCard>
            <ChartCard eyebrow="Issue lens" title="Status breakdown" testid="chart-issues-status">
              <DonutChart data={charts.issues_by_status} testid="donut-issues-status" />
            </ChartCard>
            <ChartCard eyebrow="Solution lens" title="Effort × Impact matrix" testid="chart-solution-matrix">
              <SolutionMatrix cells={charts.solution_matrix} testid="matrix-solutions" />
            </ChartCard>
          </div>

          {/* Tables row */}
          <div className="grid lg:grid-cols-2 gap-4">
            <ChartCard eyebrow="Risk lens" title="Top 5 risks" testid="chart-top-risks">
              <TopRisksTable risks={data.risks_top || []} />
            </ChartCard>
            <ChartCard eyebrow="Issue lens" title="Top 5 open issues" testid="chart-top-issues">
              <TopIssuesTable issues={data.issues_open || []} />
            </ChartCard>
          </div>
        </div>
      )}
    </div>
  );
}
